/**
 * ProjectRoadmapService — Supabase CRUD for project roadmap items
 * and kaizen week schedules.
 *
 * Rules:
 *  - Always filter by organization_id (multi-tenant safe)
 *  - Never call Supabase directly in components
 */

import { supabase } from '../lib/supabaseClient';
import type { RoadmapItem, KaizenWeekSchedule } from '../types/consultingProject';

// ─── DB row shapes ───────────────────────────────────────────

interface RoadmapRow {
  id: string;
  organization_id: string;
  project_id: string;
  title: string;
  description: string | null;
  area: string | null;
  type: string;
  status: string;
  planned_start: string;
  planned_end: string;
  actual_start: string | null;
  actual_end: string | null;
  team: string[];
  expected_gains: string | null;
  linked_kaizen_id: string | null;
  order: number;
  created_at: string;
  updated_at: string;
}

interface KaizenScheduleRow {
  id: string;
  organization_id: string;
  project_id: string;
  folder_id: string;
  week_label: string | null;
  activities: KaizenWeekSchedule[];   // full week array stored as JSONB
  created_at: string;
  updated_at: string;
}

// ─── Mappers ─────────────────────────────────────────────────

function rowToRoadmapItem(r: RoadmapRow): RoadmapItem {
  return {
    id: r.id,
    projectId: r.project_id,
    title: r.title,
    description: r.description ?? undefined,
    area: r.area ?? '',
    type: r.type as RoadmapItem['type'],
    status: r.status as RoadmapItem['status'],
    plannedStart: r.planned_start,
    plannedEnd: r.planned_end,
    actualStart: r.actual_start ?? undefined,
    actualEnd: r.actual_end ?? undefined,
    team: r.team ?? [],
    expectedGains: r.expected_gains ?? undefined,
    linkedKaizenId: r.linked_kaizen_id ?? undefined,
    order: r.order,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function roadmapItemToRow(
  item: Omit<RoadmapItem, 'id' | 'createdAt' | 'updatedAt'>,
  orgId: string,
): Omit<RoadmapRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    organization_id: orgId,
    project_id: item.projectId,
    title: item.title,
    description: item.description ?? null,
    area: item.area ?? null,
    type: item.type,
    status: item.status,
    planned_start: item.plannedStart,
    planned_end: item.plannedEnd,
    actual_start: item.actualStart ?? null,
    actual_end: item.actualEnd ?? null,
    team: item.team ?? [],
    expected_gains: item.expectedGains ?? null,
    linked_kaizen_id: item.linkedKaizenId ?? null,
    order: item.order,
  };
}

// ─── RoadmapItem Service ─────────────────────────────────────

export class ProjectRoadmapService {
  static async list(orgId: string, projectId: string): Promise<RoadmapItem[]> {
    const { data, error } = await supabase
      .from('project_roadmap_items')
      .select('*')
      .eq('organization_id', orgId)
      .eq('project_id', projectId)
      .order('order', { ascending: true });

    if (error) throw new Error(error.message);
    return (data as RoadmapRow[]).map(rowToRoadmapItem);
  }

  static async create(
    orgId: string,
    item: Omit<RoadmapItem, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<RoadmapItem> {
    const row = roadmapItemToRow(item, orgId);
    const { data, error } = await supabase
      .from('project_roadmap_items')
      .insert(row)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return rowToRoadmapItem(data as RoadmapRow);
  }

  static async update(
    orgId: string,
    id: string,
    updates: Partial<Omit<RoadmapItem, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>>,
  ): Promise<RoadmapItem> {
    const patch: Partial<RoadmapRow> = {};
    if (updates.title         !== undefined) patch.title           = updates.title;
    if (updates.description   !== undefined) patch.description     = updates.description ?? null;
    if (updates.area          !== undefined) patch.area            = updates.area ?? null;
    if (updates.type          !== undefined) patch.type            = updates.type;
    if (updates.status        !== undefined) patch.status          = updates.status;
    if (updates.plannedStart  !== undefined) patch.planned_start   = updates.plannedStart;
    if (updates.plannedEnd    !== undefined) patch.planned_end     = updates.plannedEnd;
    if (updates.actualStart   !== undefined) patch.actual_start    = updates.actualStart ?? null;
    if (updates.actualEnd     !== undefined) patch.actual_end      = updates.actualEnd ?? null;
    if (updates.team          !== undefined) patch.team            = updates.team;
    if (updates.expectedGains !== undefined) patch.expected_gains  = updates.expectedGains ?? null;
    if (updates.linkedKaizenId !== undefined) patch.linked_kaizen_id = updates.linkedKaizenId ?? null;
    if (updates.order         !== undefined) patch.order           = updates.order;

    const { data, error } = await supabase
      .from('project_roadmap_items')
      .update(patch)
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return rowToRoadmapItem(data as RoadmapRow);
  }

  static async delete(orgId: string, id: string): Promise<void> {
    const { error } = await supabase
      .from('project_roadmap_items')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId);

    if (error) throw new Error(error.message);
  }
}

// ─── KaizenSchedule Service ──────────────────────────────────
// Each row represents the full week schedule for one DMS folder.
// The `activities` JSONB column stores KaizenWeekSchedule[] (all 5 days).

export interface KaizenScheduleRecord {
  id: string;
  folderId: string;
  weekLabel: string | null;
  schedules: KaizenWeekSchedule[];
  createdAt: string;
  updatedAt: string;
}

interface KaizenScheduleRow {
  id: string;
  organization_id: string;
  project_id: string;
  folder_id: string;
  week_label: string | null;
  activities: KaizenWeekSchedule[];   // stored as JSONB
  created_at: string;
  updated_at: string;
}

function rowToRecord(r: KaizenScheduleRow): KaizenScheduleRecord {
  return {
    id: r.id,
    folderId: r.folder_id,
    weekLabel: r.week_label,
    schedules: r.activities,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export class ProjectKaizenScheduleService {
  /** Load the week schedule for a folder (one row = one folder). */
  static async getByFolder(
    orgId: string,
    projectId: string,
    folderId: string,
  ): Promise<KaizenScheduleRecord | null> {
    const { data, error } = await supabase
      .from('project_kaizen_schedules')
      .select('*')
      .eq('organization_id', orgId)
      .eq('project_id', projectId)
      .eq('folder_id', folderId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? rowToRecord(data as KaizenScheduleRow) : null;
  }

  /** Save (upsert) the full schedule array for a folder. */
  static async save(
    orgId: string,
    projectId: string,
    folderId: string,
    schedules: KaizenWeekSchedule[],
    existingId?: string | null,
    weekLabel?: string | null,
  ): Promise<KaizenScheduleRecord> {
    if (existingId) {
      const { data, error } = await supabase
        .from('project_kaizen_schedules')
        .update({ activities: schedules, week_label: weekLabel ?? null })
        .eq('id', existingId)
        .eq('organization_id', orgId)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return rowToRecord(data as KaizenScheduleRow);
    }

    const { data, error } = await supabase
      .from('project_kaizen_schedules')
      .insert({
        organization_id: orgId,
        project_id: projectId,
        folder_id: folderId,
        week_label: weekLabel ?? null,
        activities: schedules,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return rowToRecord(data as KaizenScheduleRow);
  }

  static async delete(orgId: string, id: string): Promise<void> {
    const { error } = await supabase
      .from('project_kaizen_schedules')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId);

    if (error) throw new Error(error.message);
  }
}
